import React, { useMemo } from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, DropdownMenu, Icon, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';

import { UnderstandingReturnsDialog } from './understanding-returns/UnderstandingReturnsDialog';

import { DateRangeOption } from '../../../../../graphql/schema';
import { titleCss } from '../../../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../../../Shared/urlBuilder';
import { formatReturn, formatReturnColor } from '../../../../shared/utils';
import { ReturnType } from '../AccountsCard';
import { PerformanceAccountMoneyWeightedDocument } from '../graphql/performanceAccountMoneyWeighted.generated';
import { PerformanceAccountTimeWeightedDocument } from '../graphql/performanceAccountTimeWeighted.generated';

// Shared layout constants
const ICON_SIZE = 'size.450';
const CARET_WIDTH = 'size.300';
const COLUMN_WIDTH = '33%';

const iconCss = { width: ICON_SIZE, height: ICON_SIZE, flexShrink: 0 };
const accountColumnCss = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' };
const percentColumnCss = { width: COLUMN_WIDTH, textAlign: 'right', flexShrink: 0 };
const balanceColumnCss = {
  width: COLUMN_WIDTH,
  textAlign: 'right',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  marginRight: '-space.100',
};
const caretCss = { width: CARET_WIDTH, flexShrink: 0 };

const RETURN_TYPE_LABELS: Record<ReturnType, string> = {
  'time-weighted': 'Time-weighted return',
  'money-weighted': 'Money-weighted return',
};

export const InvestingAccountRowHeader: React.FC<{
  returnType: ReturnType;
  onReturnTypeChange: (returnType: ReturnType) => void;
}> = ({ returnType, onReturnTypeChange }) => {
  return (
    <>
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 'space.150',
          paddingX: 'space.300',
          paddingY: 'space.150',
        }}
        aria-label="Column headers"
      >
        <Box css={accountColumnCss}>
          <Text variant="text50">Account</Text>
        </Box>
        <Box css={{ width: ICON_SIZE, flexShrink: 0 }} />
        <Box css={percentColumnCss}>
          <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Text variant="text50" css={{ whiteSpace: 'nowrap' }}>
              {RETURN_TYPE_LABELS[returnType]}
            </Text>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild={true}>
                <Button
                  variant="ghostPrimary"
                  css={{
                    paddingX: 'space.25',
                    paddingY: 0,
                    minWidth: 'unset',
                    minHeight: 0,
                    color: 'icon',
                  }}
                  aria-label="Change return type"
                >
                  <Icon name="CaretDown" size="xxsmall" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <Box css={{ paddingY: 'space.100' }}>
                  <DropdownMenu.Item onSelect={() => onReturnTypeChange('time-weighted')}>
                    Time-weighted return
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={() => onReturnTypeChange('money-weighted')}>
                    Money-weighted return
                  </DropdownMenu.Item>
                </Box>
                <Separator />
                <Box css={{ paddingY: 'space.100', paddingX: 'space.200' }}>
                  <UnderstandingReturnsDialog>
                    <Text>Learn more</Text>
                  </UnderstandingReturnsDialog>
                </Box>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        </Box>
        <Box css={balanceColumnCss}>
          <Text variant="text50">Balance</Text>
        </Box>
        <Box css={caretCss} aria-hidden="true" />
      </Box>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
    </>
  );
};

export const InvestingAccountRow: React.FC<{
  accountId: string;
  dateRangeOption: DateRangeOption;
  returnType: ReturnType;
}> = ({ accountId, dateRangeOption, returnType }) => {
  const queryDocument = useMemo(
    () =>
      returnType === 'time-weighted' ? PerformanceAccountTimeWeightedDocument : PerformanceAccountMoneyWeightedDocument,
    [returnType]
  );

  const { data } = useGraphQLQuery(queryDocument, {
    variables: { accountIds: [accountId], dateRangeOption },
    throwOnError: true,
  });

  const rateOfReturn = useMemo(() => {
    if (!data?.performance.totals) return undefined;
    const { totals } = data.performance;
    if (returnType === 'time-weighted' && 'timeWeightedRateOfReturn' in totals && totals.timeWeightedRateOfReturn) {
      return (totals.timeWeightedRateOfReturn as { cumulative: number }).cumulative;
    }
    if (returnType === 'money-weighted' && 'moneyWeightedRateOfReturn' in totals && totals.moneyWeightedRateOfReturn) {
      return (totals.moneyWeightedRateOfReturn as { cumulative: number }).cumulative;
    }
    return undefined;
  }, [returnType, data]);

  if (!data) return <CardLoader />;

  const account = data.performance.accounts?.[0];
  if (!account) return null;

  const { id, icon: accountIcon, name: accountName, envelope } = account;
  const { purpose } = envelope;
  const name = purpose?.name || accountName;
  const icon = purpose?.image || accountIcon;
  const balance = data.performance.totals.endingBalance;
  const earned = data.performance.totals.investing?.earned;

  return (
    <>
      <Box className="group">
        <SambaAutoLink
          to={urlBuilder.appAccountPerformance.absolute({ accountId: id })}
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'space.150',
            padding: 'space.300',
            width: '100%',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
            '&:focus': { textDecoration: 'none' },
          }}
          aria-label={name}
        >
          <Box css={iconCss}>
            <ThemedImage themedAsset={icon} alt="" aria-hidden="true" />
          </Box>

          <Box css={accountColumnCss}>
            <Text color="text" css={titleCss}>
              {name}
            </Text>
            {purpose && (
              <Text color="text.weak" variant="text50" css={{ whiteSpace: 'nowrap' }}>
                {accountName}
              </Text>
            )}
          </Box>

          <Box css={percentColumnCss}>
            <Text css={{ textAlign: 'right', color: formatReturnColor(rateOfReturn) }}>
              {formatReturn(rateOfReturn)}
            </Text>
          </Box>

          <Box css={balanceColumnCss}>
            <Text css={{ textAlign: 'right', color: 'text' }}>{formatMoney(balance)}</Text>
            {earned && (
              <Text variant="text50" css={{ textAlign: 'right', color: earned.cents > 0 ? 'text.success' : 'text' }}>
                {formatMoney(earned)}
              </Text>
            )}
          </Box>

          <Box css={caretCss}>
            <Icon color="icon" name="CaretRight" size="small" aria-hidden="true" />
          </Box>
        </SambaAutoLink>
      </Box>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
    </>
  );
};
